 import { 
  MCPConfigTemplate, 
  MCPServer, 
  GeneratorOptions, 
  ConfigValidationResult,
  PlanApp, 
  Connection,
  App 
} from '../types';

export class MCPConfigurationGenerator {
  private baseUrl: string;

  constructor(baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787') {
    this.baseUrl = baseUrl;
  }

  /**
   * Generate MCP configuration from planned connections
   */
  generateConfig(
    plannedApps: PlanApp[],
    connections: Connection[],
    options: GeneratorOptions
  ): MCPConfigTemplate {
    const { templateType, baseUrl, includeEnv, clientType } = options;

    switch (templateType) {
      case 'dedicated':
        return this.generateDedicatedConfig(plannedApps, connections, options);
      case 'remote':
        return this.generateRemoteConfig(plannedApps, connections, options);
      case 'mixed':
        return this.generateMixedConfig(plannedApps, connections, options);
      default:
        throw new Error(`Unsupported template type: ${templateType}`);
    }
  }

  /**
   * Generate dedicated MCP servers (one per account)
   */
  private generateDedicatedConfig(
    plannedApps: PlanApp[],
    connections: Connection[],
    options: GeneratorOptions
  ): MCPConfigTemplate {
    const mcpServers: Record<string, MCPServer> = {};

    plannedApps.forEach(planApp => {
      const appConnections = connections.filter(conn => conn.appId === planApp.app.id);
      
      appConnections.forEach((connection, index) => {
        const serverName = this.generateServerName(planApp.app, connection.accountName, index);
        const server = this.createDedicatedServer(planApp.app, connection, options);
        mcpServers[serverName] = server;
      });
    });

    return { mcpServers };
  }

  /**
   * Generate remote MCP servers (SSE endpoints)
   */
  private generateRemoteConfig(
    plannedApps: PlanApp[],
    connections: Connection[],
    options: GeneratorOptions
  ): MCPConfigTemplate {
    const mcpServers: Record<string, MCPServer> = {};
    const serverUrl = options.baseUrl || this.baseUrl;

    plannedApps.forEach(planApp => {
      const appConnections = connections.filter(conn => conn.appId === planApp.app.id);
      
      appConnections.forEach((connection, index) => {
        const serverName = this.generateServerName(planApp.app, connection.accountName, index);
        const server = this.createRemoteServer(planApp.app, connection, serverUrl, options);
        mcpServers[serverName] = server;
      });
    });

    return { mcpServers };
  }

  /**
   * Generate mixed configuration (dedicated for some, remote for others)
   */
  private generateMixedConfig(
    plannedApps: PlanApp[],
    connections: Connection[],
    options: GeneratorOptions
  ): MCPConfigTemplate {
    const mcpServers: Record<string, MCPServer> = {};
    const serverUrl = options.baseUrl || this.baseUrl;

    // Use remote for popular apps, dedicated for others
    const remoteApps = ['gmail', 'slack', 'github', 'google-sheets', 'notion'];

    plannedApps.forEach(planApp => {
      const appConnections = connections.filter(conn => conn.appId === planApp.app.id);
      const useRemote = remoteApps.includes(planApp.app.pipedreamAppId);
      
      appConnections.forEach((connection, index) => {
        const serverName = this.generateServerName(planApp.app, connection.accountName, index);
        const server = useRemote 
          ? this.createRemoteServer(planApp.app, connection, serverUrl, options)
          : this.createDedicatedServer(planApp.app, connection, options);
        mcpServers[serverName] = server;
      });
    });

    return { mcpServers };
  }

  /**
   * Create dedicated MCP server configuration
   */
  private createDedicatedServer(
    app: App,
    connection: Connection,
    options: GeneratorOptions
  ): MCPServer {
    const server: MCPServer = {
      command: 'npx',
      args: ['@pipedream/mcp'],
    };

    if (options.includeEnv) {
      server.env = {
        PIPEDREAM_APP: app.pipedreamAppId,
        PIPEDREAM_ACCOUNT_ID: connection.pipedreamAccountId,
        PIPEDREAM_MODE: 'stdio',
      };
    }

    return server;
  }

  /**
   * Create remote MCP server configuration (SSE)
   */
  private createRemoteServer(
    app: App,
    connection: Connection,
    serverUrl: string,
    options: GeneratorOptions
  ): MCPServer {
    const externalUserId = this.generateExternalUserId(connection);
    const endpoint = `${serverUrl}/${externalUserId}/${app.pipedreamAppId}`;

    const server: MCPServer = {
      command: 'npx',
      args: ['@modelcontextprotocol/server-everything', '--url', endpoint],
    };

    if (options.includeEnv) {
      server.env = {
        MCP_SERVER_URL: endpoint,
        PIPEDREAM_APP: app.pipedreamAppId,
        EXTERNAL_USER_ID: externalUserId,
      };
    }

    return server;
  }

  /**
   * Generate unique server name
   */
  private generateServerName(app: App, accountName?: string, index = 0): string {
    const baseName = app.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    const suffix = accountName ? `-${accountName.toLowerCase().replace(/[^a-z0-9]/g, '-')}` : '';
    const indexSuffix = index > 0 ? `-${index + 1}` : '';
    return `${baseName}${suffix}${indexSuffix}`;
  }

  /**
   * Generate external user ID for SSE endpoints
   */
  private generateExternalUserId(connection: Connection): string {
    return `user_${connection.userId}_${connection.profileId}`;
  }

  /**
   * Validate MCP configuration
   */
  validateConfig(config: MCPConfigTemplate): ConfigValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    // Check if config has servers
    if (!config.mcpServers || Object.keys(config.mcpServers).length === 0) {
      errors.push('Configuration must contain at least one MCP server');
    }

    // Validate each server
    Object.entries(config.mcpServers).forEach(([name, server]) => {
      // Check required fields
      if (!server.command) {
        errors.push(`Server "${name}" is missing required "command" field`);
      }

      // Check server name format
      if (!/^[a-z0-9-]+$/.test(name)) {
        warnings.push(`Server name "${name}" should use lowercase letters, numbers, and hyphens only`);
      }

      // Check for common issues
      if (server.command === 'npx' && (!server.args || server.args.length === 0)) {
        errors.push(`Server "${name}" using npx command must specify args`);
      }

      // Check environment variables
      if (server.env) {
        Object.entries(server.env).forEach(([key, value]) => {
          if (!value || value.trim() === '') {
            warnings.push(`Server "${name}" has empty environment variable "${key}"`);
          }
        });
      }
    });

    // Check for duplicate commands
    const commands = Object.values(config.mcpServers).map(s => `${s.command} ${s.args?.join(' ')}`);
    const duplicates = commands.filter((cmd, index) => commands.indexOf(cmd) !== index);
    if (duplicates.length > 0) {
      warnings.push('Found duplicate server commands - this may cause conflicts');
    }

    // Suggestions
    if (Object.keys(config.mcpServers).length > 10) {
      suggestions.push('Consider using remote mode for better performance with many servers');
    }

    const hasRemoteServers = Object.values(config.mcpServers).some(s => 
      s.args?.includes('--url') || s.args?.includes('server-everything')
    );
    
    if (hasRemoteServers) {
      suggestions.push('Remote servers require internet connectivity - ensure your endpoints are accessible');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestions,
    };
  }

  /**
   * Convert configuration to JSON string
   */
  toJSON(config: MCPConfigTemplate, pretty = true): string {
    return JSON.stringify(config, null, pretty ? 2 : 0);
  }

  /**
   * Convert configuration to YAML string
   */
  toYAML(config: MCPConfigTemplate): string {
    // Simple YAML conversion - in production, use a proper YAML library
    const yamlLines: string[] = ['mcpServers:'];
    
    Object.entries(config.mcpServers).forEach(([name, server]) => {
      yamlLines.push(`  ${name}:`);
      yamlLines.push(`    command: "${server.command}"`);
      
      if (server.args && server.args.length > 0) {
        yamlLines.push('    args:');
        server.args.forEach(arg => {
          yamlLines.push(`      - "${arg}"`);
        });
      }
      
      if (server.env && Object.keys(server.env).length > 0) {
        yamlLines.push('    env:');
        Object.entries(server.env).forEach(([key, value]) => {
          yamlLines.push(`      ${key}: "${value}"`);
        });
      }
      
      if (server.disabled) {
        yamlLines.push(`    disabled: ${server.disabled}`);
      }
    });
    
    return yamlLines.join('\n');
  }

  /**
   * Parse configuration from JSON string
   */
  fromJSON(jsonString: string): MCPConfigTemplate {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      throw new Error(`Invalid JSON configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get configuration presets for different client types
   */
  getClientPresets(): Record<string, Partial<GeneratorOptions>> {
    return {
      'claude-desktop': {
        templateType: 'mixed',
        includeEnv: true,
        outputFormat: 'json',
      },
      'cursor': {
        templateType: 'dedicated',
        includeEnv: false,
        outputFormat: 'json',
      },
      'zed': {
        templateType: 'remote',
        includeEnv: true,
        outputFormat: 'json',
      },
      'custom': {
        templateType: 'mixed',
        includeEnv: true,
        outputFormat: 'json',
      },
    };
  }

  /**
   * Generate example configuration
   */
  generateExample(): MCPConfigTemplate {
    return {
      mcpServers: {
        'gmail-primary': {
          command: 'npx',
          args: ['@pipedream/mcp'],
          env: {
            PIPEDREAM_APP: 'gmail',
            PIPEDREAM_ACCOUNT_ID: 'acc_abc123',
            PIPEDREAM_MODE: 'stdio',
          },
        },
        'slack-workspace': {
          command: 'npx',
          args: ['@modelcontextprotocol/server-everything', '--url', 'http://localhost:3010/user1/slack'],
          env: {
            MCP_SERVER_URL: 'http://localhost:3010/user1/slack',
            PIPEDREAM_APP: 'slack',
            EXTERNAL_USER_ID: 'user1',
          },
        },
      },
    };
  }
}

// Export singleton instance
export const configGenerator = new MCPConfigurationGenerator();